import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  MessageCircle,
  Menu,
  X,
  Check,
  Minus,
  Zap,
  Wrench,
  Sparkles,
  Search,
  ShieldCheck,
  LifeBuoy,
  Bug,
  Compass,
  Layers,
  UserCheck,
  Globe,
  Utensils,
  Hotel,
  Scale,
  Stethoscope,
  ShoppingBag,
  Gem,
  Building2,
  CalendarDays,
  Home,
  Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, RevealStagger } from "@/components/motion/Reveal";
import logoAsset from "@/assets/flashweb-logo.webp.asset.json";

const WA_URL =
  "https://wa.me/41782369200?text=Ciao%20Filippo%2C%0A%0AHo%20visto%20la%20pagina%20Servizi%20e%20vorrei%20un%27informazione%20sul%20piano%20pi%C3%B9%20adatto.%0A%0ANome%20attivit%C3%A0%3A%0ASettore%3A%0ATelefono%3A%0A%0AAttendo%20il%20tuo%20contatto.";

export const Route = createFileRoute("/servizi")({
  head: () => ({
    meta: [
      { title: "Servizi — Cosa include ogni piano | FlashWeb Agency" },
      {
        name: "description",
        content:
          "Sito professionale, sito completo o FlashWeb Flex in abbonamento: confronta cosa è incluso in ogni piano, tempi di consegna e assistenza post-lancio.",
      },
      { property: "og:title", content: "Servizi — FlashWeb Agency" },
      {
        property: "og:description",
        content:
          "Confronta i piani FlashWeb: Flex in abbonamento, Sito Professionale e Sito Completo. Tempi, feature e assistenza senza ambiguità.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.flash-web.ch/servizi" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/servizi" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.flash-web.ch/" },
            { "@type": "ListItem", position: 2, name: "Servizi", item: "https://www.flash-web.ch/servizi" },
          ],
        }),
      },
    ],
  }),
  component: ServiziPage,
});

// ─── Navbar (matches home / chi-siamo) ───
function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/50 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 group">
          <img
            src={logoAsset.url}
            alt="FlashWeb Agency"
            width="144"
            height="36"
            className="h-9 w-auto brightness-110 contrast-125 transition-transform group-hover:scale-105"
          />
          <span className="font-display text-lg font-bold tracking-tight">FlashWeb</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="hero" size="default" asChild className="hidden sm:inline-flex fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight />
            </a>
          </Button>
          <Button variant="hero" size="sm" asChild className="sm:hidden fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <button
            type="button"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/60 text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/50 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-3 text-sm">
            {[
              { href: "/", label: "Home" },
              { href: "/servizi", label: "Servizi" },
              { href: "/prezzi", label: "Prezzi" },
              { href: "/come-funziona", label: "Come funziona" },
              { href: "/portfolio", label: "Portfolio" },
              { href: "/recensioni", label: "Recensioni" },
              { href: "/trasparenza", label: "Trasparenza" },
              { href: "/chi-siamo", label: "Chi siamo" },
              { href: "/faq", label: "FAQ" },
              { href: "/blog", label: "Blog" },
              { href: "/contatti", label: "Contatti" },
              { href: "/portal", label: "Area clienti" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

// ─── Footer (matches home / chi-siamo) ───
function Footer() {
  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <img src={logoAsset.url} alt="FlashWeb Agency" width="112" height="28" className="h-7 w-auto" />
              <span className="font-display text-sm font-bold">FlashWeb Agency</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} — Tutti i diritti riservati.
            </p>
            <p className="text-sm text-muted-foreground">
              Un brand di <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sedi</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                <span className="font-semibold text-foreground">Bareerstrasse 75</span><br />
                6300 Zug, Svizzera
              </p>
              <p>
                <span className="font-semibold text-foreground">Via Ai Prati 2</span><br />
                Bedano, Svizzera
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contatti</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                <a href="mailto:info@flash-web.ch" className="font-semibold text-foreground hover:underline">info@flash-web.ch</a>
              </p>
              <p>
                <a href="https://wa.me/41782369200" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:underline">WhatsApp +41 78 236 92 00</a>
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Informazioni legali</h4>
            <p className="text-sm text-muted-foreground">
              N° registro commercio:<br />
              <span className="font-semibold text-foreground">CH-170.4.022.832-9</span>
            </p>
            <div className="flex flex-col gap-1.5 pt-1 text-sm">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">Cookie Policy</Link>
              <Link to="/termini-condizioni" className="text-muted-foreground hover:text-foreground">Termini e Condizioni</Link>
              <Link to="/impressum" className="text-muted-foreground hover:text-foreground">Impressum</Link>
            </div>
          </div>
        </div>
        <nav aria-label="Naviga" className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 pt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <Link to="/servizi" className="hover:text-foreground">Servizi</Link>
          <Link to="/prezzi" className="hover:text-foreground">Prezzi</Link>
          <Link to="/come-funziona" className="hover:text-foreground">Come funziona</Link>
          <Link to="/portfolio" className="hover:text-foreground">Portfolio</Link>
          <Link to="/recensioni" className="hover:text-foreground">Recensioni</Link>
          <Link to="/trasparenza" className="hover:text-foreground">Trasparenza</Link>
          <Link to="/chi-siamo" className="hover:text-foreground">Chi siamo</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <Link to="/contatti" className="hover:text-foreground">Contatti</Link>
        </nav>
        <div className="mt-6 flex flex-col gap-2 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} FVT FuturaVision Technology GmbH — Tutti i diritti riservati.</p>
          <p>Realizzato con precisione svizzera 🇨🇭</p>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════ COMPARISON DATA ═══════════════
type Cell = string | boolean;
type PlanKey = "flex" | "pro" | "completo";

const PLANS: { key: PlanKey; name: string; price: string; sub: string; featured?: boolean }[] = [
  { key: "flex", name: "FlashWeb Flex", price: "CHF 59/mese", sub: "Abbonamento tutto incluso" },
  { key: "pro", name: "Sito Professionale", price: "CHF 990", sub: "Una tantum" },
  { key: "completo", name: "Sito Completo", price: "CHF 1'790", sub: "Una tantum", featured: true },
];

const ROWS: { label: string; values: Record<PlanKey, Cell> }[] = [
  { label: "Numero pagine", values: { flex: "1 pagina", pro: "1 pagina", completo: "Fino a 5 pagine" } },
  { label: "Tempo di consegna", values: { flex: "48 ore", pro: "48 ore", completo: "72 ore" } },
  { label: "Design su misura", values: { flex: true, pro: true, completo: "Premium" } },
  { label: "SEO on-page", values: { flex: true, pro: true, completo: "Avanzata" } },
  { label: "Animazioni & integrazioni", values: { flex: false, pro: false, completo: true } },
  { label: "Dashboard cliente", values: { flex: false, pro: false, completo: true } },
  { label: "Hosting & dominio", values: { flex: "Su richiesta", pro: "Su richiesta", completo: "Su richiesta" } },
  { label: "Aggiornamenti mensili", values: { flex: "Illimitati", pro: "A pagamento", completo: "1 mese incluso" } },
  { label: "Assistenza", values: { flex: "Prioritaria", pro: "Email", completo: "Dedicata 30 gg" } },
  { label: "Revisioni incluse", values: { flex: "Illimitate", pro: "1", completo: "2" } },
  { label: "Prezzo", values: { flex: "CHF 59/mese", pro: "CHF 990", completo: "CHF 1'790" } },
];

function renderCell(v: Cell) {
  if (v === true) return <Check className="mx-auto h-5 w-5 text-primary" aria-label="Incluso" />;
  if (v === false) return <Minus className="mx-auto h-5 w-5 text-muted-foreground/50" aria-label="Non incluso" />;
  return <span className="text-sm">{v}</span>;
}

// ═══════════════ PAGE ═══════════════
function ServiziPage() {
  const [activePlan, setActivePlan] = useState<PlanKey>("completo");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* 1. HERO */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-4xl px-6 py-24 text-center sm:py-32">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              Servizi
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl md:text-7xl">
              Tre modi di lavorare insieme. <span className="text-gradient">Zero ambiguità.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground sm:text-xl">
              Ogni piano include hosting, dominio e prima assistenza. La differenza è
              quanto vuoi che ci pensiamo noi.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" asChild className="fw-shimmer">
                <a href="#confronto">
                  Confronta i piani <ArrowRight />
                </a>
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-5 w-5" /> Chiedi in 30 secondi
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2. COMPARISON TABLE */}
      <section id="confronto" className="relative mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
        <Reveal>
          <div className="mb-12 max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              Confronto piani
            </div>
            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              Cosa include <span className="text-gradient">esattamente</span> ogni piano.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Nessuna riga in piccolo. Se non è in tabella, non è nel prezzo.
            </p>
          </div>
        </Reveal>

        {/* Desktop table */}
        <Reveal>
          <div className="hidden md:block">
            <div className="overflow-hidden rounded-3xl border border-border/60 bg-card/30 backdrop-blur-xl">
              <div className="sticky top-16 z-20 grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-0 border-b border-border/60 bg-card/80 backdrop-blur-xl">
                <div className="p-6" />
                {PLANS.map((p) => (
                  <div
                    key={p.key}
                    className={`relative p-6 text-center ${
                      p.featured ? "bg-primary/5" : ""
                    }`}
                  >
                    {p.featured && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-primary to-fuchsia-500 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground shadow-[0_10px_30px_-5px_var(--primary)]">
                        Più scelto
                      </span>
                    )}
                    <div className="font-display text-lg font-bold">{p.name}</div>
                    <div className="mt-1 text-2xl font-bold text-gradient">{p.price}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{p.sub}</div>
                  </div>
                ))}
              </div>
              {ROWS.map((r, i) => (
                <div
                  key={r.label}
                  className={`grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-0 border-b border-border/40 last:border-0 ${
                    i % 2 === 0 ? "bg-transparent" : "bg-background/30"
                  }`}
                >
                  <div className="p-5 text-sm font-semibold text-foreground/90">{r.label}</div>
                  {PLANS.map((p) => (
                    <div
                      key={p.key}
                      className={`p-5 text-center text-foreground/90 ${
                        p.featured ? "bg-primary/5" : ""
                      }`}
                    >
                      {renderCell(r.values[p.key])}
                    </div>
                  ))}
                </div>
              ))}
              <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-0 border-t border-border/60 bg-card/40 p-4">
                <div />
                {PLANS.map((p) => (
                  <div key={p.key} className="px-2">
                    <Button
                      variant={p.featured ? "hero" : "outlineHero"}
                      size="sm"
                      asChild
                      className={`w-full ${p.featured ? "fw-shimmer" : ""}`}
                    >
                      <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                        Scegli <ArrowRight className="h-3.5 w-3.5" />
                      </a>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Mobile: tab switcher + single card */}
        <div className="md:hidden">
          <div className="mb-4 flex gap-2 overflow-x-auto rounded-2xl border border-border/60 bg-card/40 p-1.5">
            {PLANS.map((p) => (
              <button
                key={p.key}
                onClick={() => setActivePlan(p.key)}
                className={`flex-1 whitespace-nowrap rounded-xl px-3 py-2 text-xs font-semibold transition-all ${
                  activePlan === p.key
                    ? "bg-gradient-to-r from-primary to-fuchsia-500 text-primary-foreground shadow-[0_10px_30px_-10px_var(--primary)]"
                    : "text-muted-foreground"
                }`}
              >
                {p.name.replace("Sito ", "").replace("FlashWeb ", "")}
              </button>
            ))}
          </div>
          {PLANS.filter((p) => p.key === activePlan).map((p) => (
            <div
              key={p.key}
              className={`rounded-3xl border border-border/60 bg-card/40 p-6 backdrop-blur-xl ${
                p.featured ? "ring-1 ring-primary/40" : ""
              }`}
            >
              {p.featured && (
                <span className="inline-block rounded-full bg-gradient-to-r from-primary to-fuchsia-500 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">
                  Più scelto
                </span>
              )}
              <div className="mt-3 font-display text-2xl font-bold">{p.name}</div>
              <div className="mt-1 text-3xl font-bold text-gradient">{p.price}</div>
              <div className="text-xs text-muted-foreground">{p.sub}</div>
              <ul className="mt-6 divide-y divide-border/40">
                {ROWS.map((r) => (
                  <li key={r.label} className="flex items-center justify-between gap-4 py-3">
                    <span className="text-sm text-muted-foreground">{r.label}</span>
                    <span className="text-right font-semibold text-foreground">
                      {renderCell(r.values[p.key])}
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                variant={p.featured ? "hero" : "outlineHero"}
                size="lg"
                asChild
                className={`mt-6 w-full ${p.featured ? "fw-shimmer" : ""}`}
              >
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  Scegli {p.name} <ArrowRight />
                </a>
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* 3. MANTENIMENTO */}
      <section className="relative border-y border-border bg-card/40">
        <div className="pointer-events-none absolute inset-0 opacity-50" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto w-full max-w-6xl px-6 py-20 sm:py-28">
          <Reveal>
            <div className="mb-12 max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
                Post-lancio
              </div>
              <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Non ti lasciamo <span className="text-gradient">mai da solo.</span>
              </h2>
              <p className="mt-4 text-muted-foreground">
                Il sito è online. Ora inizia il lavoro vero: qui trovi come funziona
                l'assistenza reale, con tempi e processo scritti nero su bianco.
              </p>
            </div>
          </Reveal>

          <RevealStagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" stagger={0.08}>
            {[
              {
                Icon: Bug,
                t: "Correzione bug",
                d: "Un pulsante non risponde, un form non invia, un'immagine è sfocata.",
                p: "Apri un ticket dal Portale Assistenza: rispondiamo entro 4 ore lavorative e risolviamo in giornata.",
              },
              {
                Icon: Wrench,
                t: "Modifiche testi e contenuti",
                d: "Nuovo orario, nuovo prezzo, un paragrafo da riscrivere.",
                p: "Ci scrivi cosa cambiare, aggiorniamo il sito e ti mandiamo l'anteprima entro 24h lavorative.",
              },
              {
                Icon: Sparkles,
                t: "Nuove funzionalità",
                d: "Un modulo prenotazioni, un'area membri, un blog, una lingua in più.",
                p: "Ti mandiamo un preventivo trasparente in 48h. Se sei su Flex, molte estensioni sono già incluse.",
              },
              {
                Icon: Search,
                t: "Ottimizzazione SEO",
                d: "Nuove pagine da posizionare, meta tag da rivedere, contenuti da rinforzare.",
                p: "Analisi trimestrale con report leggibile e azioni concrete, non un PDF di 40 pagine.",
              },
              {
                Icon: ShieldCheck,
                t: "Sicurezza e backup",
                d: "Certificato SSL, aggiornamenti software, backup automatici quotidiani.",
                p: "Tutto gestito da noi in background. Ti avvisiamo solo se serve un'azione da parte tua.",
              },
              {
                Icon: LifeBuoy,
                t: "Supporto diretto",
                d: "Nessun ticket numerato, nessun bot. Parli con chi ha costruito il tuo sito.",
                p: "Email, WhatsApp o Portale Assistenza: scegli il canale, la risposta è sempre di una persona.",
              },
            ].map(({ Icon, t, d, p }) => (
              <div
                key={t}
                className="group rounded-2xl border border-border bg-background/70 p-6 backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-[0_20px_60px_-20px_var(--primary)]"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold">{t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{d}</p>
                <p className="mt-3 border-t border-border/50 pt-3 text-sm text-foreground/80">{p}</p>
              </div>
            ))}
          </RevealStagger>

          <Reveal>
            <div className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="lg" asChild className="fw-shimmer">
                <a href="/portal">
                  Vai al Portale Assistenza <ArrowRight />
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4. SETTORI */}
      <section className="relative mx-auto w-full max-w-6xl px-6 py-20 sm:py-28">
        <Reveal>
          <div className="mb-12 max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              Settori serviti
            </div>
            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              Lavoriamo con chi <span className="text-gradient">costruisce cose vere.</span>
            </h2>
            <p className="mt-4 text-muted-foreground">
              Ogni settore ha il suo linguaggio. Clicca sul tuo per vedere esempi reali.
            </p>
          </div>
        </Reveal>

        <RevealStagger className="flex flex-wrap gap-3" stagger={0.04}>
          {[
            { Icon: Utensils, label: "Ristorazione" },
            { Icon: Hotel, label: "Hôtellerie" },
            { Icon: Scale, label: "Studi legali" },
            { Icon: Stethoscope, label: "Cliniche estetiche" },
            { Icon: ShoppingBag, label: "Retail & E-commerce" },
            { Icon: Gem, label: "Artigianato di lusso" },
            { Icon: Building2, label: "Aziende B2B" },
            { Icon: CalendarDays, label: "Eventi" },
            { Icon: Home, label: "Immobiliare" },
            { Icon: Briefcase, label: "Servizi professionali" },
          ].map(({ Icon, label }) => (
            <a
              key={label}
              href={`/#showcase`}
              className="group inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-4 py-2.5 text-sm font-medium text-foreground/90 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:bg-primary/5 hover:text-foreground"
            >
              <Icon className="h-4 w-4 text-primary" />
              {label}
            </a>
          ))}
        </RevealStagger>
      </section>

      {/* 5. PROCESSO */}
      <section className="relative border-y border-border bg-card/40">
        <div className="pointer-events-none absolute inset-0 opacity-50" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto w-full max-w-4xl px-6 py-20 sm:py-28">
          <Reveal>
            <div className="mb-14 max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
                Il processo
              </div>
              <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Quattro step. <span className="text-gradient">Quarantotto ore.</span>
              </h2>
              <p className="mt-4 text-muted-foreground">
                Nessun preventivo infinito, nessuna call da due ore. E dopo il lancio, ci
                siamo ancora.
              </p>
            </div>
          </Reveal>

          <ol className="relative border-l border-border/60 pl-8 sm:pl-10">
            {[
              {
                Icon: Compass,
                title: "Briefing guidato",
                time: "15 minuti",
                desc: "Un questionario intelligente raccoglie tutto: obiettivi, contenuti, referenze visive.",
              },
              {
                Icon: Layers,
                title: "Costruzione in parallelo",
                time: "24–36 ore",
                desc: "Design, contenuti e SEO on-page nello stesso sprint. Niente attese tra reparti.",
              },
              {
                Icon: UserCheck,
                title: "Revisione con il founder",
                time: "in giornata",
                desc: "Parli direttamente con Filippo. Modifiche approvate in tempo reale, sito online entro le 48h.",
              },
              {
                Icon: Globe,
                title: "Continuiamo insieme",
                time: "assistenza inclusa",
                desc: "Con Flex ti seguiamo mese dopo mese; sui piani una tantum hai comunque 30 giorni di manutenzione inclusa più il Portale Assistenza sempre attivo.",
              },
            ].map(({ Icon, title, time, desc }, i) => (
              <li key={title} className="relative mb-10 last:mb-0">
                <Reveal delay={i * 0.08}>
                  <span className="absolute -left-[46px] flex h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-background text-primary shadow-[0_0_20px_-4px_var(--primary)] sm:-left-[54px]">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="rounded-2xl border border-border bg-background/70 p-6 backdrop-blur transition-colors hover:border-primary/50">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <h3 className="font-display text-xl font-semibold tracking-tight">
                        0{i + 1}. {title}
                      </h3>
                      <span className="text-xs font-semibold uppercase tracking-widest text-primary">
                        {time}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground sm:text-base">{desc}</p>
                  </div>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="relative mx-auto w-full max-w-4xl px-6 py-20 sm:py-28">
        <Reveal>
          <div className="mb-12 max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              Domande frequenti
            </div>
            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              Le cose che <span className="text-gradient">nessuno chiede</span> su WhatsApp.
            </h2>
          </div>
        </Reveal>

        <div className="space-y-4">
          {[
            {
              q: "Chi possiede il codice sorgente del sito?",
              a: "Sui piani una tantum (Professionale e Completo) il codice è tuo: al saldo ti consegniamo un archivio completo del progetto. Su Flex il sito resta sulla nostra infrastruttura finché l'abbonamento è attivo — puoi comunque richiedere l'export in qualsiasi momento e passare a un piano una tantum.",
            },
            {
              q: "Posso passare da un piano all'altro?",
              a: "Sì, in entrambi i sensi. Da Flex a un sito una tantum: scomputiamo dal prezzo del sito i mesi già pagati. Da un sito una tantum a Flex: attivi l'abbonamento e assorbiamo il tuo sito nella manutenzione.",
            },
            {
              q: "Cosa succede se supero le revisioni incluse?",
              a: "Ogni revisione extra sul piano Professionale costa CHF 90 forfait, sul piano Completo CHF 60. Su Flex le revisioni sono illimitate — l'unico limite è il buon senso: se una modifica richiede più di 4h di lavoro te lo comunichiamo prima e decidiamo insieme.",
            },
            {
              q: "L'hosting e il dominio a chi sono intestati?",
              a: "Sempre a te. Sui piani una tantum il primo anno è incluso e poi rinnovi direttamente col provider (~CHF 15/anno dominio + CHF 60/anno hosting). Su Flex tutto è dentro l'abbonamento e non paghi mai niente in più.",
            },
          ].map((f) => (
            <Reveal key={f.q}>
              <details className="group rounded-2xl border border-border bg-card/50 p-6 backdrop-blur transition-colors open:border-primary/50">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
                  <span className="font-display text-lg font-semibold text-foreground">
                    {f.q}
                  </span>
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border text-primary transition-transform group-open:rotate-45">
                    <ArrowRight className="h-4 w-4 rotate-[-45deg] group-open:rotate-0" />
                  </span>
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {f.a}
                </p>
              </details>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-10 text-center">
            <Button variant="outlineHero" size="lg" asChild>
              <a href="/#faq">
                Vedi tutte le FAQ <ArrowRight />
              </a>
            </Button>
          </div>
        </Reveal>
      </section>

      {/* 7. CTA FINALE */}
      <section className="relative border-t border-border bg-card/40">
        <div className="pointer-events-none absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-4xl px-6 py-20 text-center sm:py-28">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary backdrop-blur">
              <Zap className="h-3 w-3" />
              Pronto in 48 ore
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              Ancora un dubbio? <span className="text-gradient">Scrivici.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Ti rispondiamo su WhatsApp in giornata con il piano più adatto — senza call,
              senza impegno.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" asChild className="fw-shimmer">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-5 w-5" /> Scrivici su WhatsApp
                </a>
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <a href="/#price">
                  Configura il tuo sito <ArrowRight />
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
